import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, DollarSign, Clock, Leaf, ShieldAlert, Sparkles } from "lucide-react";

export default function RoiCalculator() {
  const [weeklyRevenue, setWeeklyRevenue] = useState(14000);
  const [locations, setLocations] = useState(1);
  const [currentWastePct, setCurrentWastePct] = useState(4.5); // % of revenue lost to waste
  // Adjustable assumptions — these are YOUR targets, not guaranteed outcomes
  const [wasteCutPct, setWasteCutPct] = useState(20); // % of waste you aim to recover
  const [laborHoursSaved, setLaborHoursSaved] = useState(3); // hours/week per store you aim to reclaim
  const [laborRate, setLaborRate] = useState(32); // $/hr

  // Calculations
  const monthlyRevenue = weeklyRevenue * 4.33 * locations;
  const currentMonthlyWaste = monthlyRevenue * (currentWastePct / 100);
  const wasteSavings = currentMonthlyWaste * (wasteCutPct / 100);
  const laborSavings = laborHoursSaved * 4.33 * laborRate * locations;

  const totalMonthlyBenefit = wasteSavings + laborSavings;
  const planCost = locations === 1 ? 199 : 499;
  const netMonthlyRoi = totalMonthlyBenefit - planCost;
  const annualSavings = netMonthlyRoi * 12;
  const roiMultiple = (totalMonthlyBenefit / planCost).toFixed(1);

  return (
    <section className="max-w-6xl mx-auto px-6 py-20 md:py-28">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-2xl mx-auto"
      >
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-3.5 py-1.5 rounded-full">
          <Calculator className="h-3.5 w-3.5" /> ROI & Profit Calculator
        </div>
        <h2 className="font-display font-semibold text-3xl md:text-4xl text-zinc-900 mt-4">
          Calculate your café's profit recovery
        </h2>
        <p className="text-zinc-500 mt-3 text-base">
          See how much food waste, stockout revenue, and management hours Optics saves your team every month.
        </p>
      </motion.div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
        {/* Left Side: Interactive Sliders */}
        <div className="lg:col-span-6 bg-white border border-zinc-200/90 rounded-2xl p-6 md:p-8 shadow-sm flex flex-col justify-between space-y-6">
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-zinc-800">
                  Weekly Café Revenue
                </label>
                <span className="font-display font-bold text-lg text-zinc-900 tnum">
                  NZ${weeklyRevenue.toLocaleString()} <span className="text-xs text-zinc-400 font-normal">/wk</span>
                </span>
              </div>
              <input
                type="range"
                min="4000"
                max="50000"
                step="1000"
                value={weeklyRevenue}
                onChange={(e) => setWeeklyRevenue(Number(e.target.value))}
                className="w-full accent-zinc-900 h-2 bg-zinc-100 rounded-lg cursor-pointer"
              />
              <div className="flex justify-between text-[11px] text-zinc-400 mt-1">
                <span>$4k/wk (Boutique)</span>
                <span>$20k/wk (High-Volume)</span>
                <span>$50k/wk (Flagship)</span>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-zinc-800">
                  Number of Café Locations
                </label>
                <span className="font-display font-bold text-lg text-zinc-900 tnum">
                  {locations} {locations === 1 ? "Store" : "Stores"}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 5].map((num) => (
                  <button
                    key={num}
                    onClick={() => setLocations(num)}
                    className={`py-2 text-xs font-semibold rounded-lg border transition-colors ${
                      locations === num
                        ? "bg-zinc-900 text-white border-zinc-900 shadow-sm"
                        : "bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                    }`}
                  >
                    {num} {num === 1 ? "Store" : "Stores"}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-zinc-800">
                  Estimated Current Waste Rate
                </label>
                <span className="font-display font-bold text-lg text-amber-600 tnum">
                  {currentWastePct.toFixed(1)}% <span className="text-xs text-zinc-400 font-normal">of sales</span>
                </span>
              </div>
              <input
                type="range"
                min="2.0"
                max="8.0"
                step="0.5"
                value={currentWastePct}
                onChange={(e) => setCurrentWastePct(Number(e.target.value))}
                className="w-full accent-amber-500 h-2 bg-zinc-100 rounded-lg cursor-pointer"
              />
              <div className="text-[11px] text-zinc-400 mt-1">
                Industry average for fresh cafés is 4.0% – 5.5% (pastries, milk, batch brew)
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-zinc-800">
                  Target Waste Recovery
                </label>
                <span className="font-display font-bold text-lg text-amber-600 tnum">
                  {wasteCutPct}% <span className="text-xs text-zinc-400 font-normal">of waste</span>
                </span>
              </div>
              <input
                type="range"
                min="5"
                max="50"
                step="5"
                value={wasteCutPct}
                onChange={(e) => setWasteCutPct(Number(e.target.value))}
                className="w-full accent-amber-500 h-2 bg-zinc-100 rounded-lg cursor-pointer"
              />
              <div className="text-[11px] text-zinc-400 mt-1">
                Set the share of current waste you aim to recover through better forecasting
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-zinc-800">
                  Hours Reclaimed / Store / Week
                </label>
                <span className="font-display font-bold text-lg text-zinc-900 tnum">
                  {laborHoursSaved} <span className="text-xs text-zinc-400 font-normal">hrs</span>
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="8"
                step="1"
                value={laborHoursSaved}
                onChange={(e) => setLaborHoursSaved(Number(e.target.value))}
                className="w-full accent-zinc-900 h-2 bg-zinc-100 rounded-lg cursor-pointer"
              />
              <div className="text-[11px] text-zinc-400 mt-1">
                Time saved on ordering, stocktakes and guesswork
              </div>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-amber-50/70 border border-amber-200/80 text-xs text-amber-900 flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-amber-600 shrink-0" />
            <span>
              This is an illustrative scenario based on the targets you set — adjust the sliders to model your own café.
            </span>
          </div>
        </div>

        {/* Right Side: Estimated Returns Breakdown */}
        <div className="lg:col-span-6 bg-zinc-950 text-white rounded-2xl p-6 md:p-8 shadow-xl flex flex-col justify-between border border-zinc-800">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
              Estimated Monthly Profit Lift
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-display text-4xl md:text-5xl font-bold text-emerald-400 tnum">
                +${Math.round(netMonthlyRoi).toLocaleString()}
              </span>
              <span className="text-zinc-400 text-sm">/month net gain</span>
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              Equivalent to <strong className="text-white">+${Math.round(annualSavings).toLocaleString()} NZD</strong> bottom-line profit per year.
            </div>

            {/* Benefit Pillars */}
            <div className="mt-8 space-y-4">
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-zinc-900/80 border border-zinc-800">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-red-950/60 border border-red-800/40 grid place-items-center">
                    <Leaf className="h-4 w-4 text-red-400" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-zinc-200">Waste Reduction ({wasteCutPct}% target)</div>
                    <div className="text-[11px] text-zinc-400">Less end-of-day dumping</div>
                  </div>
                </div>
                <div className="text-sm font-bold text-white tnum">
                  +${Math.round(wasteSavings).toLocaleString()}/mo
                </div>
              </div>

              <div className="flex items-center justify-between p-3.5 rounded-xl bg-zinc-900/80 border border-zinc-800">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-sky-950/60 border border-sky-800/40 grid place-items-center">
                    <Clock className="h-4 w-4 text-sky-400" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-zinc-200">Ordering Time Saved ({laborHoursSaved} hrs/wk)</div>
                    <div className="text-[11px] text-zinc-400">Automated PO generation</div>
                  </div>
                </div>
                <div className="text-sm font-bold text-white tnum">
                  +${Math.round(laborSavings).toLocaleString()}/mo
                </div>
              </div>
            </div>
          </div>

          <div className="pt-8">
            <Link
              to="/login"
              className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 shadow-lg shadow-emerald-950/50"
            >
              Start Free 14-Day Trial & Recover Your Profit <ArrowRight className="h-4 w-4" />
            </Link>
            <div className="text-center text-[11px] text-zinc-500 mt-2">
              14-day free trial · card required · cancel anytime · setup in 3 minutes
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
